import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { studioInfo } from '../mock';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Chi Sono', path: '/about' },
    { name: 'Servizi', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Tecnologie', path: '/technologies' },
    { name: 'Contatti', path: '/contact' }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group" data-testid="nav-logo">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative w-11 h-11 flex-shrink-0"
              >
                <div className="w-full h-full bg-transparent border-2 border-[#D4AF37] rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors duration-300">
                  <span className="text-[#D4AF37] font-bold text-lg tracking-tighter" style={{fontFamily: 'Playfair Display, Georgia, serif'}}>GP</span>
                </div>
              </motion.div>
              <div className="block border-l border-white/10 pl-2 sm:pl-3">
                <p className="text-white font-medium text-sm sm:text-base leading-tight" style={{fontFamily: 'Playfair Display, Georgia, serif'}}>Gabriele Pastore</p>
                <p className="text-gray-400 text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.25em] uppercase mt-0.5 sm:mt-1 font-light">Studio Tecnico</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="relative px-4 py-2 text-sm font-medium transition-colors duration-300 group"
                >
                  <span
                    className={`relative z-10 ${
                      location.pathname === link.path
                        ? 'text-[#D4AF37]'
                        : 'text-gray-300 group-hover:text-white'
                    }`}
                  >
                    {link.name}
                  </span>
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute inset-0 bg-white/5 rounded-lg"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* Contact Info */}
            <div className="hidden xl:flex items-center space-x-4">
              <a
                href={`tel:${studioInfo.phone}`}
                className="flex items-center space-x-2 text-gray-300 hover:text-[#D4AF37] transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm">{studioInfo.phone}</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white hover:text-[#D4AF37] transition-colors duration-300"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-20 left-0 right-0 bg-[#0A1628]/98 backdrop-blur-lg z-40 lg:hidden border-b border-white/5"
          >
            <div className="px-4 py-6 space-y-1">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
                      location.pathname === link.path
                        ? 'bg-white/10 text-[#D4AF37]'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-4 mt-4 border-t border-white/10 space-y-3">
                <a
                  href={`tel:${studioInfo.phone}`}
                  className="flex items-center space-x-3 px-4 py-2 text-gray-300 hover:text-[#D4AF37] transition-colors duration-300"
                >
                  <Phone className="w-5 h-5" />
                  <span>{studioInfo.phone}</span>
                </a>
                <a
                  href={`mailto:${studioInfo.email}`}
                  className="flex items-center space-x-3 px-4 py-2 text-gray-300 hover:text-[#D4AF37] transition-colors duration-300"
                >
                  <Mail className="w-5 h-5" />
                  <span>{studioInfo.email}</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
